import { Injectable, inject } from "@angular/core";
import {Storage} from "@ionic/storage-angular";
import {CoreStorageBaseService} from "@core/storage/core-storage-base.service";
import {ISharedLoginStoreState} from "@shared/components/login/store/shared-login.state";
import {EAppType} from '@shared/models/AppType.enum';
import {QrTokenDto} from '@server-models';

@Injectable({
  providedIn: 'root',
})
export class SharedLoginStorageService extends CoreStorageBaseService<ISharedLoginStoreState> {
  protected key: string = 'sharedLogin';
  private _storage = inject(Storage);

  constructor() {
    const storage = inject(Storage);

    super(storage);
  }

  async saveLogin(state: ISharedLoginStoreState): Promise<void> {
    const {tempEmail, tempPassword, ...storedState} = state;
    if (state.currentApp === EAppType.Link) {
      const resourceId = (state.currentToken as QrTokenDto)?.resourceId;
      if (resourceId && resourceId > 0) {
        await this.setCustomKey(this._resourceKey(resourceId), storedState);
      }
      return;
    }
    await this.set(storedState);
  }

  async loadLogin(appType?: EAppType, resourceId?: number): Promise<ISharedLoginStoreState | undefined> {
    if (appType !== EAppType.Link) return this.get();
    if (!resourceId || resourceId <= 0) return undefined;

    const stored = await this.getCustomKey(this._resourceKey(resourceId));
    if (stored) return this._matchesResource(stored, resourceId) ? stored : undefined;

    // Only migrate the legacy shared login when it belongs to the requested resource.
    const legacy = await this.get();
    if (!this._matchesResource(legacy, resourceId)) return undefined;
    await this.saveLogin(legacy);
    return legacy;
  }

  async removeLogin(state: ISharedLoginStoreState): Promise<void> {
    if (state.currentApp !== EAppType.Link) {
      await this.remove();
      return;
    }
    const resourceId = (state.currentToken as QrTokenDto)?.resourceId;
    if (!resourceId || resourceId <= 0) return;
    await this._storage.remove(this._resourceKey(resourceId));
    // Prevent the old shared entry from restoring a logged-out resource.
    if (this._matchesResource(await this.get(), resourceId)) await this.remove();
  }

  private _matchesResource(state: ISharedLoginStoreState | undefined, resourceId: number): boolean {
    return state?.currentApp === EAppType.Link &&
      (state.currentToken as QrTokenDto)?.resourceId === resourceId;
  }

  private _resourceKey(resourceId: number): string {
    return `${this.key}:link:${resourceId}`;
  }
}
